import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { API_BASE, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatINR, fmtDateTime } from "@/lib/roles";
import {
  Loader2, ArrowLeft, Upload, FileText, Download, Building, User, MapPin, ClipboardCheck,
  ShieldCheck, Trash2, PlusCircle, CheckCircle2, XCircle,
} from "lucide-react";
import { StatusBadge } from "./BuildingPlansList";

const STATUS_TRANSITIONS = {
  submitted: ["under_scrutiny", "rejected"],
  under_scrutiny: ["inspection_scheduled", "needs_revision", "rejected"],
  inspection_scheduled: ["inspection_completed", "rejected"],
  inspection_completed: ["approved", "rejected", "needs_revision"],
  needs_revision: ["submitted", "rejected"],
  approved: [],
  rejected: [],
};
const TRANS_LABEL = {
  under_scrutiny: "Move to scrutiny",
  inspection_scheduled: "Schedule inspection",
  inspection_completed: "Mark inspection complete",
  approved: "Approve",
  rejected: "Reject",
  needs_revision: "Request revision",
  submitted: "Resubmit",
};

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-2 py-2 border-b border-slate-100 dark:border-slate-800 last:border-b-0">
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm font-medium">{value || "—"}</div>
    </div>
  );
}

export default function BuildingPlanDetail() {
  const { aid } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isCitizen = user?.role === "citizen";
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const [showTransition, setShowTransition] = useState(false);
  const [transitionTarget, setTransitionTarget] = useState("");
  const [transitionRemarks, setTransitionRemarks] = useState("");
  const [transitionLoading, setTransitionLoading] = useState(false);

  const [showInspection, setShowInspection] = useState(false);
  const [inspection, setInspection] = useState({ remarks: "", outcome: "satisfactory" });
  const [inspectionLoading, setInspectionLoading] = useState(false);

  const listPath = isCitizen ? "/portal/building-plans" : "/building-plans";

  useEffect(() => {
    api.get(`/building-plans/${aid}`)
      .then((r) => setApp(r.data))
      .catch((err) => {
        const status = err.response?.status;
        setError(status === 403 ? "You do not have access to this application." : (status === 404 ? "Application not found." : "Unable to load application."));
      })
      .finally(() => setLoading(false));
  }, [aid]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (error) {
    return (
      <div className="max-w-lg mx-auto py-20 text-center" data-testid="bp-detail-error">
        <XCircle className="w-12 h-12 text-red-500 mx-auto" />
        <h2 className="text-xl font-heading font-semibold mt-4">{error}</h2>
        <p className="text-sm text-slate-500 mt-2">If you believe this is a mistake, please contact your municipal officer.</p>
        <Link to={listPath} className="inline-block mt-6">
          <Button variant="outline"><ArrowLeft className="w-4 h-4 mr-1" /> Back to list</Button>
        </Link>
      </div>
    );
  }
  if (!app) return <div className="text-center py-10">Not found.</div>;

  const uploadDrawing = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/building-plans/${app.id}/drawings`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Drawing uploaded");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const deleteDrawing = async (did) => {
    try {
      await api.delete(`/building-plans/${app.id}/drawings/${did}`);
      toast.success("Drawing removed");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const downloadDrawing = (d) => {
    const token = localStorage.getItem("pulb_token");
    fetch(`${API_BASE}/building-plans/${app.id}/drawings/${d.id}`, {
      credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => r.blob())
      .then((b) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(b);
        link.download = d.original_name || d.stored_name;
        link.click();
      });
  };

  const downloadCertificate = () => {
    const token = localStorage.getItem("pulb_token");
    fetch(`${API_BASE}/building-plans/${app.id}/certificate.pdf`, {
      credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => r.blob())
      .then((b) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(b);
        link.download = `BP_Certificate_${app.certificate_number || app.application_id}.pdf`;
        link.click();
      });
  };

  const submitTransition = async (e) => {
    e.preventDefault();
    setTransitionLoading(true);
    try {
      await api.patch(`/building-plans/${app.id}/status`, { status: transitionTarget, remarks: transitionRemarks });
      toast.success(`Status → ${transitionTarget.replace("_", " ")}`);
      setShowTransition(false);
      setTransitionTarget(""); setTransitionRemarks("");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setTransitionLoading(false);
  };

  const submitInspection = async (e) => {
    e.preventDefault();
    setInspectionLoading(true);
    try {
      await api.post(`/building-plans/${app.id}/inspections`, inspection);
      toast.success("Inspection recorded");
      setShowInspection(false);
      setInspection({ remarks: "", outcome: "satisfactory" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setInspectionLoading(false);
  };

  const nextStates = STATUS_TRANSITIONS[app.status] || [];
  const canOfficerAct = !isCitizen && nextStates.length > 0;
  const canUpload = app.status === "submitted" || app.status === "needs_revision" || !isCitizen;
  const canDeleteDrawings = (app.status === "submitted" || app.status === "needs_revision");
  // Refetch helper for post-mutation reloads
  const load = () => {
    setLoading(true);
    setError(null);
    api.get(`/building-plans/${aid}`)
      .then((r) => setApp(r.data))
      .catch((err) => {
        const status = err.response?.status;
        setError(status === 403 ? "You do not have access to this application." : (status === 404 ? "Application not found." : "Unable to load application."));
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="space-y-6" data-testid="bp-detail-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Link to={listPath}><Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button></Link>
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Application</div>
            <h1 className="text-xl sm:text-2xl font-heading font-semibold flex items-center gap-2">
              <Building className="w-5 h-5" /> {app.application_id}
              <StatusBadge status={app.status} />
            </h1>
          </div>
        </div>
        <div className="flex gap-2">
          {app.status === "approved" && (
            <Button className="bg-[#0D7650] hover:bg-[#0a5d3f]" onClick={downloadCertificate} data-testid="btn-download-certificate">
              <Download className="w-4 h-4 mr-1" /> Download certificate
            </Button>
          )}
          {canOfficerAct && (
            <Button className="bg-[#0F2942] hover:bg-[#1E3A5F]" onClick={() => setShowTransition(true)} data-testid="btn-change-status">
              <CheckCircle2 className="w-4 h-4 mr-1" /> Change status
            </Button>
          )}
          {!isCitizen && app.status === "inspection_scheduled" && (
            <Button variant="outline" onClick={() => setShowInspection(true)} data-testid="btn-add-inspection">
              <ClipboardCheck className="w-4 h-4 mr-1" /> Record inspection
            </Button>
          )}
        </div>
      </div>

      {/* Applicant & construction info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2" data-testid="bp-info-card">
          <CardHeader><CardTitle className="text-base font-heading flex items-center gap-2"><User className="w-4 h-4" /> Application details</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
            <Row label="Applicant" value={app.applicant_name} />
            <Row label="Contact" value={`${app.applicant_email || "—"} · ${app.applicant_phone || "—"}`} />
            <Row label="Architect" value={app.architect_name} />
            <Row label="Architect license" value={app.architect_license_no} />
            <Row label="Property type" value={<span className="capitalize">{app.property_type}</span>} />
            <Row label="Purpose" value={<span className="capitalize">{app.purpose?.replace("_", " ")}</span>} />
            <Row label="Plot area" value={`${app.plot_area} sq.ft`} />
            <Row label="Built-up area" value={`${app.built_up_area} sq.ft`} />
            <Row label="Floors" value={app.floors} />
            <Row label="Estimated cost" value={formatINR(app.estimated_cost)} />
            <Row label="Address" value={app.address} />
            <Row label="Ward / Zone" value={`${app.ward} / ${app.zone}`} />
            <Row label="Municipal body" value={app.municipal_body} />
            <Row label="Coordinates" value={`${app.latitude?.toFixed(5)}, ${app.longitude?.toFixed(5)}`} />
          </CardContent>
        </Card>
        <Card data-testid="bp-fee-status-card">
          <CardHeader><CardTitle className="text-base font-heading">Fee & approval</CardTitle></CardHeader>
          <CardContent>
            <div className="text-xs uppercase tracking-wider text-slate-500">Scrutiny fee</div>
            <div className="text-2xl font-heading font-bold tabular-nums text-[#0F2942] dark:text-white">{formatINR(app.fee_amount)}</div>
            <div className="mt-2 text-xs">
              {app.fee_paid ? <Badge className="bg-[#0D7650]">Paid</Badge> : <Badge variant="secondary">Not paid</Badge>}
            </div>
            {app.status === "approved" && (
              <div className="mt-6">
                <div className="text-xs uppercase tracking-wider text-slate-500">Certificate no.</div>
                <div className="font-mono text-sm font-semibold">{app.certificate_number}</div>
                <div className="text-xs text-slate-500 mt-1">Approved {fmtDateTime(app.approved_at)}</div>
              </div>
            )}
            {app.status === "rejected" && app.rejection_reason && (
              <div className="mt-6">
                <div className="text-xs uppercase tracking-wider text-slate-500">Rejection reason</div>
                <div className="text-sm text-red-600">{app.rejection_reason}</div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Drawings */}
      <Card data-testid="bp-drawings-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base font-heading flex items-center gap-2"><FileText className="w-4 h-4" /> Drawings & documents</CardTitle>
          {canUpload && (
            <div>
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.dwg,.dxf"
                className="hidden"
                onChange={uploadDrawing}
                data-testid="bp-drawing-input"
              />
              <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="btn-upload-drawing">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                Upload drawing
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {(app.drawings || []).length === 0 ? (
            <div className="p-6 text-sm text-slate-500 text-center border border-dashed rounded-lg">No drawings uploaded yet. Accepted: PDF, PNG, JPG, DWG, DXF (max 15 MB each).</div>
          ) : (
            <div className="divide-y">
              {app.drawings.map((d) => (
                <div key={d.id} className="py-3 flex items-center justify-between gap-3" data-testid={`drawing-${d.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded bg-[#0F2942]/10 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-4 h-4 text-[#0F2942]" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{d.original_name || d.stored_name}</div>
                      <div className="text-xs text-slate-500">{(d.size / 1024).toFixed(1)} KB · {fmtDateTime(d.uploaded_at)}</div>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => downloadDrawing(d)} data-testid={`btn-dl-drawing-${d.id}`}>
                      <Download className="w-4 h-4" />
                    </Button>
                    {canDeleteDrawings && (app.applicant_user_id === user?.id || !isCitizen) && (
                      <Button size="sm" variant="ghost" onClick={() => deleteDrawing(d.id)} className="text-red-600" data-testid={`btn-del-drawing-${d.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Inspections */}
      <Card data-testid="bp-inspections-card">
        <CardHeader><CardTitle className="text-base font-heading flex items-center gap-2"><ClipboardCheck className="w-4 h-4" /> Inspections</CardTitle></CardHeader>
        <CardContent>
          {(app.inspections || []).length === 0 ? (
            <div className="p-6 text-sm text-slate-500 text-center border border-dashed rounded-lg">No inspections recorded yet.</div>
          ) : (
            <div className="space-y-3">
              {app.inspections.map((i) => (
                <div key={i.id} className="p-3 rounded-lg border border-slate-200 dark:border-slate-800">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium">{i.inspector_name}</div>
                    <Badge variant={i.outcome === "satisfactory" ? "default" : "destructive"} className="capitalize">{i.outcome?.replace("_", " ")}</Badge>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">Actual: {fmtDateTime(i.actual_date)}</div>
                  {i.remarks && <p className="mt-2 text-sm">{i.remarks}</p>}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Workflow log */}
      <Card data-testid="bp-workflow-card">
        <CardHeader><CardTitle className="text-base font-heading flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Workflow log</CardTitle></CardHeader>
        <CardContent>
          <ol className="relative border-l border-slate-200 dark:border-slate-700 ml-3 space-y-4 pt-1">
            {(app.workflow_log || []).map((l) => (
              <li key={l.id} className="pl-6 relative">
                <span className="absolute -left-2 top-1 w-3 h-3 rounded-full bg-[#0F2942] border-2 border-white dark:border-slate-900" />
                <div className="text-sm font-medium">
                  {l.action === "created" ? "Application created" : `Status → ${(l.to_status || "").replace("_", " ")}`}
                </div>
                <div className="text-xs text-slate-500">
                  {l.actor_email || "system"} · {l.actor_role} · {fmtDateTime(l.at)}
                </div>
                {l.remarks && <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">{l.remarks}</p>}
              </li>
            ))}
          </ol>
        </CardContent>
      </Card>

      {/* Status change dialog */}
      <Dialog open={showTransition} onOpenChange={setShowTransition}>
        <DialogContent>
          <DialogHeader><DialogTitle>Change application status</DialogTitle></DialogHeader>
          <form onSubmit={submitTransition} className="space-y-3">
            <div>
              <Label>Move to</Label>
              <Select value={transitionTarget} onValueChange={setTransitionTarget}>
                <SelectTrigger data-testid="select-transition"><SelectValue placeholder="Choose next status" /></SelectTrigger>
                <SelectContent>
                  {nextStates.map((s) => (
                    <SelectItem key={s} value={s}>{TRANS_LABEL[s] || s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Remarks (optional)</Label>
              <Textarea value={transitionRemarks} onChange={(e) => setTransitionRemarks(e.target.value)} rows={3} data-testid="transition-remarks" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={!transitionTarget || transitionLoading} className="bg-[#0F2942]" data-testid="btn-confirm-transition">
                {transitionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Inspection dialog */}
      <Dialog open={showInspection} onOpenChange={setShowInspection}>
        <DialogContent>
          <DialogHeader><DialogTitle>Record inspection</DialogTitle></DialogHeader>
          <form onSubmit={submitInspection} className="space-y-3">
            <div>
              <Label>Outcome</Label>
              <Select value={inspection.outcome} onValueChange={(v) => setInspection({ ...inspection, outcome: v })}>
                <SelectTrigger data-testid="inspection-outcome"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="satisfactory">Satisfactory</SelectItem>
                  <SelectItem value="needs_revision">Needs revision</SelectItem>
                  <SelectItem value="unsatisfactory">Unsatisfactory</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Remarks</Label>
              <Textarea required rows={4} value={inspection.remarks} onChange={(e) => setInspection({ ...inspection, remarks: e.target.value })} data-testid="inspection-remarks" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={inspectionLoading} className="bg-[#0F2942]" data-testid="btn-submit-inspection">
                {inspectionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save inspection"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
